import { HelpHero } from 'helphero';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { StringKeyMap } from '../../../adl-gen/common';
import { DbKey, WithDbId } from '../../../adl-gen/common/db';
import {
  AppUser,
  Competency,
  CompetencyBarsSlideout,
  CompetencySkillTrainingOffering,
  RoleRecommendationMethod,
  TrainingOffering,
  TrainingOfferingCategory,
  WorkRole
} from '../../../adl-gen/totalinteraction/futurefit/db';
import { SingleUserTrainingRecommendations } from '../../../adl-gen/totalinteraction/futurefit/frontend';
import { withI18n, WithI18nProps } from '../../../internationalization/i18n-hoc';
import { i18next } from '../../../internationalization/i18next';
import { Loader, MaybeLoading } from '../../../loader/loader';
import { Route } from '../../../routing/app-routes';
import { LoggedIn } from '../../../stores/identity-store';
import { PlotKeyToggleState } from '../../../stores/plot-key-toggle-state';
import { CategoryOption, LearningType, QuizResultsHelper } from '../../../stores/quiz-results-store';
import { PageLayoutFn } from '../../common/page-layout/page-layout';
import { TabContainer } from '../../common/tab-container/tab-container';
import { CompetencyGapReport } from '../../components/competency-gap-report/competency-gap-report';
import { FilterOption } from '../../components/filters/common/enabled-disabled-map';
import { CompetencyFilter } from '../../components/filters/competency-filter/competency-filter';
import { LearningTypeFilter } from '../../components/filters/learning-type-filter/learning-type-filter';
import { RecommendationCategoryFilter } from '../../components/filters/recommendation-category-filter/recommendation-category-filter';
import { RolesFilter } from '../../components/filters/roles-filter/roles-filter';
import { Recommendations } from '../../components/recommendations/recommendations';
import { SelectOption } from '../../widgets/select/select';
import { QuizSummary } from '../quiz-summary/quiz-summary';

interface QuizResultsScreenProps extends WithI18nProps {
  /** Constructor for the page layout */
  pageLayout: PageLayoutFn;

  /** Goto another page */
  navigateTo(route: Route): void;

  identity: LoggedIn;

  userQuizId: string;

  propsToLoad: MaybeLoading<LoadedProps>;

  plotKeyToggleState: PlotKeyToggleState;

  selectRole(role: string): void;
  /// Enhanced Recommendations
  selectCategories(selected: TrainingOfferingCategory[]): void;
  selectCompetencies(selected: string[]): void;
  selectPriorities(selected: string[]): void;
  resetAll(): void;

  page: 'profile' | 'guidance' | 'recommendations' | 'print';

  walkThroughFn?(page: string): void;
  walkThroughMgr?: HelpHero;

  onLaunchTrainingOffering?(offering: WithDbId<TrainingOffering>, userId: DbKey<AppUser>): string;
}

export interface LoadedProps {
  /** Data response object with all quiz results and the skills competencies model */
  qr: QuizResultsHelper;
  userTrainingRecommendations: SingleUserTrainingRecommendations;
  filteredCategories: TrainingOfferingCategory[];
  filteredCompetencies: string[];
  filteredPriorities: LearningType[];
}

type ModalState = 'none' | 'roles' | 'categories' | 'competencies' | 'priorities';

@observer
class QuizResultsScreen0 extends React.Component<QuizResultsScreenProps> {
  @observable rolesSelectorIsVisible = false;
  @observable modalState: ModalState = 'none';

  @action
  setModalState = (modalState: ModalState) => {
    this.modalState = modalState;
  };

  @action
  onSelectRole = (role: string) => {
    this.props.selectRole(role);
    this.setModalState('none');
  };

  @action
  onSelectCategories = (selected: TrainingOfferingCategory[]) => {
    this.props.selectCategories(selected);
    this.setModalState('none');
  };

  @action
  onSelectCompetencies = (qr: QuizResultsHelper) => (selected: string[]) => {
    /// Find skills assoc with these competencies
    const skills = selected.map(qr.getSkillsByCompetency).reduce((arr, next) => arr.concat(next), []);
    this.props.selectCompetencies([...selected, ...skills]);
    this.setModalState('none');
  };

  @action
  onSelectPriorities = (selected: string[]) => {
    this.props.selectPriorities(selected);
    this.setModalState('none');
  };

  @action
  onResetAll = () => {
    this.props.resetAll();
    this.setModalState('none');
  };

  setUpWalkThrough = (qr: QuizResultsHelper) => {
    const { quiz, guidanceAvailable, recommendationsAvailable } = qr;
    const { identity, page, walkThroughFn, walkThroughMgr } = this.props;
    const defaultRole = qr.getDefaultRole();
    const recommendationMethod = defaultRole
      ? defaultRole.value.recommendationMethod === RoleRecommendationMethod.roleRange
        ? 'roleRange'
        : 'relevance'
      : 'relevance';
    const selectedRole = qr.getSelectedRoleId() ? [qr.getSelectedRoleId()] : [];
    const rolesSelected = selectedRole.length > 0;
    if (walkThroughMgr) {
      walkThroughMgr.update({ noGuidance: !guidanceAvailable() });
      walkThroughMgr.update({ noRecommendations: !recommendationsAvailable() });
      walkThroughMgr.update({ noSkillBars: quiz.competencyBars === CompetencyBarsSlideout.noSkills });
      walkThroughMgr.update({ noCompetencyBarsSlideout: quiz.competencyBars === CompetencyBarsSlideout.none });
      walkThroughMgr.update({ recommendationMethod });
      walkThroughMgr.update({ rolesSelected });
      /// TODO: Update walkthroughs
      walkThroughMgr.update({ enhancedRecommendations: identity.appClient.singleTabRecommendations });
    }
    if (walkThroughFn) {
      walkThroughFn(page);
    }
  };

  getRecommendationMethod = (qr: QuizResultsHelper): RoleRecommendationMethod => {
    const defaultRole = qr.getDefaultRole();
    if (defaultRole) {
      return defaultRole.value.recommendationMethod;
    }
    /// default to relevance
    return RoleRecommendationMethod.relevance;
  };

  setRoleTabInfo = (
    userTrainingRecommendations: SingleUserTrainingRecommendations,
    title: string,
    roleId: DbKey<WorkRole>,
    key: number,
    qr: QuizResultsHelper,
    categories: CategoryOption[],
    tags: SelectOption[]
  ) => {
    const { identity, onLaunchTrainingOffering } = this.props;
    const userTraining = this.filterTrainingOfferingsOnRole(userTrainingRecommendations, roleId);
    if (userTraining) {
      return {
        key,
        title,
        content: (
          <Recommendations
            userTraining={userTraining}
            categories={categories}
            categoryFilter={'external-controlled'}
            priorityFilter={qr.priorityFilter}
            enabled={identity.appClient.recommendationsVisible}
            launchUrl={onLaunchTrainingOffering}
            tags={tags}
          />
        ),
        selected: true,
        disabled: false
      };
    } else {
      return {
        key,
        title,
        content: '',
        selected: false,
        disabled: true
      };
    }
  };

  filterTrainingOfferingsOnRole = (
    userTrainingRecommendations: SingleUserTrainingRecommendations,
    roleId: DbKey<WorkRole>
  ): SingleUserTrainingRecommendations | undefined => {
    const { user, data } = userTrainingRecommendations;
    const recommendations = user.recommendations.filter(
      rec => rec.result.kind === 'roleScore' && rec.result.value.workRoleId === roleId
    );
    if (recommendations.length > 0) {
      const roleCompetencySkillMap: StringKeyMap<string, CompetencySkillTrainingOffering[]> = {};
      for (const [k, v] of Object.entries(user.competencySkillMap)) {
        const includedRec = recommendations.find(rec =>
          v.map(item => item.value.trainingOfferingId).includes(rec.trainingOfferingId)
        );
        if (includedRec) {
          roleCompetencySkillMap[k] = user.competencySkillMap[k];
        }
      }
      return {
        user: {
          appUserId: user.appUserId,
          competencySkillMap: roleCompetencySkillMap,
          recommendations
        },
        data
      };
    } else {
      return undefined;
    }
  };

  /// Run this after userQuizTrainingOfferings.data has been used for anything else
  /// (eg. finding out whether a category is available) just before rendering
  filterAvailableTrainingOfferings = (state: LoadedProps): StringKeyMap<string, TrainingOffering> => {
    // TODO: Curry these using closures
    const filteredData = this.filterAvailableTrainingOfferingsOnCategory(state);
    return this.filterAvailableTrainingOfferingsOnCompetency(state, filteredData);
  };

  filterAvailableTrainingOfferingsOnCategory = (state: LoadedProps): StringKeyMap<string, TrainingOffering> => {
    const { filteredCategories, userTrainingRecommendations: userQuizTrainingOfferings } = state;
    const { data } = userQuizTrainingOfferings;
    if (filteredCategories.length > 0) {
      const filteredData: StringKeyMap<string, TrainingOffering> = Object.entries(data).reduce(
        (acc: StringKeyMap<string, TrainingOffering>, [k, v]) => {
          if (filteredCategories.includes(v.category)) {
            acc[k] = v;
          }
          return acc;
        },
        {}
      );
      return filteredData;
    }
    return data;
  };
  filterAvailableTrainingOfferingsOnCompetency = (
    state: LoadedProps,
    prefilteredData: StringKeyMap<string, TrainingOffering>
  ) => {
    const { filteredCompetencies, userTrainingRecommendations } = state;
    const data = prefilteredData;
    if (filteredCompetencies.length > 0) {
      // Pull out competencySkillsMap into flat array and map to IDs.
      // Assume this map has already been prefiltered using the filtered competencies
      const list = Object.values(userTrainingRecommendations.user.competencySkillMap)
        .reduce((acc, next) => {
          return acc.concat(next);
        }, [])
        .map(off => off.value.trainingOfferingId);
      const filteredData: StringKeyMap<string, TrainingOffering> = Object.entries(data).reduce(
        (acc: StringKeyMap<string, TrainingOffering>, [k, v]) => {
          if (list.includes(k)) {
            acc[k] = v;
          }
          return acc;
        },
        {}
      );
      return filteredData;
    }
    return data;
  };

  renderProfilePage(qr: QuizResultsHelper) {
    const { identity } = this.props;
    return (
      <QuizSummary
        qr={qr}
        plotKeyToggleState={this.props.plotKeyToggleState}
        displayHeadlineRatio={identity.appClient.displayHeadlineRatio}
        displayHeadlineText={identity.appClient.displayHeadlineText}
      />
    );
  }

  renderGuidancePage(qr: QuizResultsHelper) {
    return (
      <div>
        <CompetencyGapReport qr={qr} />
      </div>
    );
  }

  renderTabbedPriorityRecommendationsPage(
    qr: QuizResultsHelper,
    userTrainingRecommendations: SingleUserTrainingRecommendations
  ) {
    let eLearningIncluded: boolean = false;
    let virtualIncluded: boolean = false;
    let face2faceIncluded: boolean = false;
    for (const v of Object.values(userTrainingRecommendations.data)) {
      if (v.category === TrainingOfferingCategory.elearning) {
        eLearningIncluded = true;
      } else if (v.category === TrainingOfferingCategory.virtual) {
        virtualIncluded = true;
      } else if (v.category === TrainingOfferingCategory.face2face) {
        face2faceIncluded = true;
      }
    }

    return (
      <TabContainer
        items={[
          {
            /// You can't set JSX elements as props explicitly here, because by the time they get to TabPanel, you get
            /// "Objects are not valid as a React child". This is a TS thing I think
            key: 0,
            title: i18next.t('recommendations-categories.elearning', 'Self-Paced/eLearning'),
            content: (
              <Recommendations
                userTraining={userTrainingRecommendations}
                enabled={this.props.identity.appClient.recommendationsVisible}
                categoryFilter={TrainingOfferingCategory.elearning}
                launchUrl={this.props.onLaunchTrainingOffering}
                priorityFilter={qr.priorityFilter}
              />
            ),
            selected: eLearningIncluded,
            disabled: !eLearningIncluded
          },
          {
            key: 1,
            title: i18next.t('recommendations-categories.virtual', 'Virtual Live'),
            content: (
              <Recommendations
                userTraining={userTrainingRecommendations}
                enabled={this.props.identity.appClient.recommendationsVisible}
                categoryFilter={TrainingOfferingCategory.virtual}
                launchUrl={this.props.onLaunchTrainingOffering}
                priorityFilter={qr.priorityFilter}
              />
            ),
            selected: !eLearningIncluded || virtualIncluded,
            disabled: !virtualIncluded
          },
          {
            key: 2,
            title: i18next.t('recommendations-categories.face2face', 'Face to Face'),
            content: (
              <Recommendations
                userTraining={userTrainingRecommendations}
                enabled={this.props.identity.appClient.recommendationsVisible}
                categoryFilter={TrainingOfferingCategory.face2face}
                launchUrl={this.props.onLaunchTrainingOffering}
                priorityFilter={qr.priorityFilter}
              />
            ),
            selected: !eLearningIncluded || face2faceIncluded,
            disabled: !face2faceIncluded
          }
        ]}
        selectedTabKey={eLearningIncluded ? 0 : virtualIncluded ? 1 : face2faceIncluded ? 2 : 0}
      />
    );
  }

  renderSinglePriorityRecommendationsPage(
    qr: QuizResultsHelper,
    userTrainingRecommendations: SingleUserTrainingRecommendations
  ) {
    return (
      <Recommendations
        userTraining={userTrainingRecommendations}
        enabled={this.props.identity.appClient.recommendationsVisible}
        priorityFilter={qr.priorityFilter}
        launchUrl={this.props.onLaunchTrainingOffering}
      />
    );
  }

  /// Allow filter on Roles
  /// There are basically three ways to implement this:
  /// Implem  Roles
  /// ------------------------------
  ///  A      Selector
  ///  B      Existing filter* (-> URL rewrite)
  ///  C      Tabs
  /// ------------------------------
  /// Selectors should not need reloading the page, if the observables are managed
  ///  * use filter but allow only one role to be selected at a time; this would interfere with
  ///    its action on the Profile page, if used there in the same way as for Relevance method assessments
  renderRoleRangeRecommendationsPage(
    qr: QuizResultsHelper,
    userTrainingRecommendations: SingleUserTrainingRecommendations
  ) {
    const { identity, t } = this.props;
    // Pull ComparisonRoles and Competencies

    /// Use Competencies (or Skills, if used) for Pills
    const categoryTags = qr.getTagOptions(t);
    /// Use Competencies for Category (accordion) data
    const categoryLabelsAndRatios = qr.getCategoryOptions(t);

    // Only use these if roles are actually selected for comparison.
    const selectedRoleId = qr.getSelectedRoleId();
    if (selectedRoleId) {
      const defaultRoleItem = this.setRoleTabInfo(
        userTrainingRecommendations,
        t(qr.quiz.workRole.name),
        qr.quiz.workRoleId,
        0,
        qr,
        categoryLabelsAndRatios,
        categoryTags
      );
      const roleMap = qr.getAllRoles();
      const otherRoleItems = selectedRoleId !== qr.quiz.workRoleId
        ? [this.setRoleTabInfo(
          userTrainingRecommendations,
          t(roleMap[selectedRoleId].workRole.value.name),
          selectedRoleId,
          1,
          qr,
          categoryLabelsAndRatios,
          categoryTags
        )] : [];
      return (
        <TabContainer
          items={[defaultRoleItem, ...otherRoleItems]}
          selectedTabKey={defaultRoleItem.selected ? 0 : otherRoleItems.findIndex(r => r.selected) + 1}
        />
      );
    } else {
      /// Suppress tabs altogether - BUT only serve recommendations for the default role
      const defaultUserTraining = this.filterTrainingOfferingsOnRole(userTrainingRecommendations, qr.quiz.workRoleId);
      if (defaultUserTraining) {
        return (
          <Recommendations
            userTraining={defaultUserTraining}
            categories={categoryLabelsAndRatios}
            categoryFilter={'external-controlled'}
            priorityFilter={qr.priorityFilter}
            enabled={identity.appClient.recommendationsVisible}
            launchUrl={this.props.onLaunchTrainingOffering}
            tags={categoryTags}
          />
        );
      } else {
        return null;
      }
    }
  }

  renderRecommendationsPage(loadedProps: LoadedProps) {
    const { qr, userTrainingRecommendations } = loadedProps;

    if (this.getRecommendationMethod(qr) === RoleRecommendationMethod.roleRange) {
      return this.renderRoleRangeRecommendationsPage(qr, userTrainingRecommendations);
    } else {
      const availableTrainingOfferingMap = this.filterAvailableTrainingOfferings(loadedProps);
      if (this.props.identity.appClient.singleTabRecommendations) {
        return this.renderSinglePriorityRecommendationsPage(qr, {
          ...userTrainingRecommendations,
          data: availableTrainingOfferingMap
        });
      } else {
        return this.renderTabbedPriorityRecommendationsPage(qr, {
          ...userTrainingRecommendations,
          data: availableTrainingOfferingMap
        });
      }
    }
  }

  renderPrintScreen(loadedProps: LoadedProps) {
    const { qr } = loadedProps;
    return (
      <>
        <GlobalPrint />
        {this.renderProfilePage(qr)}
        {this.renderGuidancePage(qr)}
        {this.renderRecommendationsPage(loadedProps)}
      </>
    );
  }

  renderRoleSelection(qr: QuizResultsHelper) {
    return (
      <RolesFilter
        initial={qr.getSelectedRoleId()}
        defaultRoleId={qr.quiz.workRoleId}
        otherRoleIds={qr.getOtherRoleIds()}
        rolesMap={qr.getAllRoles()}
        onDone={this.onSelectRole}
        onCancel={() => this.setModalState('none')}
      />
    );
  }

  renderCategoryFilter(loadedProps: LoadedProps, availableCategories: TrainingOfferingCategory[]) {
    const { filteredCategories } = loadedProps;
    const allCategoryIds = [
      TrainingOfferingCategory.elearning,
      TrainingOfferingCategory.face2face,
      TrainingOfferingCategory.virtual
    ];
    const defaultFilterOptionSet: FilterOption<TrainingOfferingCategory>[] = allCategoryIds.map(cat => ({
      visibility: 'disabled',
      value: cat
    }));
    const availableFilterOptionSet: FilterOption<TrainingOfferingCategory>[] = defaultFilterOptionSet.map(cat => {
      if (availableCategories.includes(cat.value)) {
        return { visibility: 'default', value: cat.value };
      }
      return cat;
    });

    const initList = filteredCategories.length === 0 ? availableCategories : filteredCategories;
    return (
      <RecommendationCategoryFilter
        initial={initList}
        options={availableFilterOptionSet}
        onDone={this.onSelectCategories}
        onCancel={() => this.setModalState('none')}
      />
    );
  }

  renderCompetencyFilter(loadedProps: LoadedProps, availableCompetencies: string[]) {
    const { t } = this.props;
    const { qr, filteredCompetencies } = loadedProps;
    const competencyMap: StringKeyMap<DbKey<Competency>, Competency> = qr
      .getCompetencyKeys(t)
      .reduce((map: StringKeyMap<DbKey<Competency>, Competency>, next) => {
        map[next] = qr.competency(next);
        return map;
      }, {});
    const defaultFilterOptionSet: FilterOption<string>[] = qr
      .getCompetencyKeys(t)
      .map(c => ({ visibility: 'disabled', value: c }));
    const availableFilterOptionSet: FilterOption<string>[] = defaultFilterOptionSet.map(cat => {
      if (availableCompetencies.includes(cat.value)) {
        return { visibility: 'default', value: cat.value };
      }
      return cat;
    });
    const initList = filteredCompetencies.length === 0 ? availableCompetencies : filteredCompetencies;
    return (
      <CompetencyFilter
        initial={initList}
        options={availableFilterOptionSet}
        competencyMap={competencyMap}
        onDone={this.onSelectCompetencies(qr)}
        onCancel={() => this.setModalState('none')}
      />
    );
  }

  renderPrioritiesFilter(loadedProps: LoadedProps) {
    const { filteredPriorities } = loadedProps;
    const allPriorities: LearningType[] = ['recommendations', 'all-learning'];
    const defaultLearning: LearningType[] = ['recommendations'];
    const initList = filteredPriorities.length === 0 ? defaultLearning : filteredPriorities;
    return (
      <LearningTypeFilter
        initial={initList}
        allPriorities={allPriorities}
        onDone={this.onSelectPriorities}
        onCancel={() => this.setModalState('none')}
      />
    );
  }

  renderQuizResultsSection = observer((loadedProps: LoadedProps) => {
    const { page, t } = this.props;
    const { singleTabRecommendations } = this.props.identity.appClient;
    const { qr, userTrainingRecommendations } = loadedProps;
    if (page === 'print') {
      return this.renderPrintScreen(loadedProps);
    }
    this.setUpWalkThrough(qr);

    /// Get all available (different) trainingOfferings categories
    const availableTrainingOfferingsCategories = Object.values(userTrainingRecommendations.data)
      .map(off => off.category)
      .reduce((acc: TrainingOfferingCategory[], next) => {
        if (!acc.includes(next)) {
          acc.push(next);
        }
        return acc;
      }, []);
    /// Get all available trainingOfferings competencies
    const flatCompetencySkillsMap = Object.values(userTrainingRecommendations.user.competencySkillMap).reduce(
      (acc, next) => {
        return acc.concat(next);
      },
      []
    );
    const availableCompetencies: string[] = flatCompetencySkillsMap
      .reduce((acc: string[], next) => {
        if (Object.keys(userTrainingRecommendations.data).includes(next.value.trainingOfferingId)) {
          if (next.kind === 'competencyTrainingOffering') {
            acc.push(next.value.competencyId);
          } else {
            const skill = qr.skill(next.value.skillId);
            if (skill) {
              acc.push(skill.competencyId);
            }
          }
        }
        return acc;
      }, [])
      // Unique
      .filter((val, idx, arr) => arr.indexOf(val) === idx);

    const selectedRoleName = qr.getSelectedRoleShortName();

    return (
      <this.props.pageLayout
        title={t(qr.quiz.title)}
        roleSelected={
          selectedRoleName ? t(selectedRoleName) : undefined
        }
        rolesAvailable={qr.getOtherRoleIds().length > 0}
        onClickRoles={() => this.setModalState('roles')}
        onClickCategories={
          page === 'recommendations' && singleTabRecommendations ? () => this.setModalState('categories') : undefined
        }
        /// SS-759: Enable these filters even for renderTabbedPriority
        onClickCompetencies={
          page === 'recommendations' &&
            this.getRecommendationMethod(qr) === RoleRecommendationMethod.relevance
            ? () => this.setModalState('competencies')
            : undefined
        }
        onClickPriorities={
          page === 'recommendations'
            ? () => this.setModalState('priorities')
            : undefined
        }
        onClickResetAll={
          page === 'recommendations'
            ? () => this.onResetAll()
            : undefined
        }
      >
        {this.modalState === 'roles' && this.renderRoleSelection(qr)}
        {this.modalState === 'categories' &&
          this.renderCategoryFilter(loadedProps, availableTrainingOfferingsCategories)}
        {this.modalState === 'competencies' && this.renderCompetencyFilter(loadedProps, availableCompetencies)}
        {this.modalState === 'priorities' && this.renderPrioritiesFilter(loadedProps)}
        {page === 'profile' && this.renderProfilePage(qr)}
        {page === 'guidance' && this.renderGuidancePage(qr)}
        {page === 'recommendations' && this.renderRecommendationsPage(loadedProps)}
      </this.props.pageLayout>
    );
  });

  render() {
    return <Loader state={this.props.propsToLoad} LoadedComponent={this.renderQuizResultsSection} />;
  }
}

export const QuizResultsScreen = withI18n(QuizResultsScreen0);

// Hacky component to trigger componentDidMount after the loader has loaded
export class GlobalPrint extends React.Component {
  static hasTriggered = false;

  componentDidMount(): void {
    if (!GlobalPrint.hasTriggered) {
      GlobalPrint.hasTriggered = true;
      window.print();
    }
  }

  render() {
    return <></>;
  }
}



// WEBPACK FOOTER //
// ./src/ui/screens/quiz-results-screen/quiz-results-screen.tsx